
import { PermissionNotification } from '@/types/permissionErrors';

/**
 * Service for managing permission-related user notifications
 */
export class PermissionNotificationService {
  private static notifications = new Map<string, PermissionNotification>();
  private static listeners = new Set<(notifications: PermissionNotification[]) => void>();

  /**
   * Add a notification
   */
  static addNotification(notification: Omit<PermissionNotification, 'id'>): string {
    const id = `perm_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    
    const fullNotification: PermissionNotification = {
      ...notification,
      id,
    };

    this.notifications.set(id, fullNotification);
    this.notifyListeners();

    // Auto-hide notification if configured
    if (notification.autoHide !== false) {
      const hideAfter = notification.hideAfter || 5000;
      setTimeout(() => {
        this.removeNotification(id);
      }, hideAfter);
    }

    console.log('🔔 Permission notification added:', notification.title);
    return id;
  }

  /**
   * Remove a notification
   */
  static removeNotification(id: string): void {
    if (this.notifications.delete(id)) {
      this.notifyListeners();
      console.log('🔔 Permission notification removed:', id);
    }
  }

  /**
   * Clear all notifications
   */
  static clearAll(): void {
    this.notifications.clear();
    this.notifyListeners();
    console.log('🔔 All permission notifications cleared');
  }

  /**
   * Get all current notifications
   */
  static getAllNotifications(): PermissionNotification[] {
    return Array.from(this.notifications.values());
  }

  /**
   * Subscribe to notification changes
   */
  static subscribe(listener: (notifications: PermissionNotification[]) => void): () => void {
    this.listeners.add(listener);
    
    // Immediately call with current notifications
    listener(this.getAllNotifications());
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of changes
   */
  private static notifyListeners(): void {
    const notifications = this.getAllNotifications();
    this.listeners.forEach(listener => {
      try {
        listener(notifications);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Create common notification types
   */
  static createOfflineNotification(): PermissionNotification {
    return {
      id: '',
      type: 'warning',
      title: 'Limited Functionality',
      message: 'You\'re offline. Some features may be unavailable.',
      autoHide: false,
      persistent: true,
    };
  }

  static createAuthErrorNotification(retry: () => void): PermissionNotification {
    return {
      id: '',
      type: 'error',
      title: 'Authentication Required',
      message: 'Your session has expired. Please sign in again.',
      actions: [
        {
          label: 'Sign In',
          action: retry,
          variant: 'default',
        }
      ],
      persistent: true,
      autoHide: false,
    };
  }

  static createAdminRevokedNotification(): PermissionNotification {
    return {
      id: '',
      type: 'error',
      title: 'Admin Access Revoked',
      message: 'Your admin privileges have been removed. Admin features are no longer available.',
      persistent: true,
      autoHide: false,
    };
  }

  static createRecoveryNotification(attempt: number, maxAttempts: number): PermissionNotification {
    return {
      id: '',
      type: 'info',
      title: 'Reconnecting...',
      message: `Attempting to restore connection (${attempt}/${maxAttempts})`,
      autoHide: true,
      hideAfter: 3000,
    };
  }

  static createPermissionDeniedNotification(action: string): PermissionNotification {
    return {
      id: '',
      type: 'warning',
      title: 'Access Denied',
      message: `You don't have permission to ${action}. Contact your administrator if you believe this is an error.`,
      autoHide: true,
      hideAfter: 8000,
    };
  }
}
